import type { TextFacet } from "../structs.ts";

export type MarkdownTokenKind =
  | "bold"
  | "italic"
  | "link-marker"
  | "link-destination"
  | "escape";

export interface MarkdownToken {
  readonly start: number;
  readonly end: number;
  readonly kind: MarkdownTokenKind;
}

export interface CompiledMarkdown {
  readonly text: string;
  readonly facets: readonly TextFacet[];
  readonly tokens: readonly MarkdownToken[];
  /** The source offset of each UTF-16 unit of `text`. */
  readonly sources: readonly number[];
}

interface ParseResult extends CompiledMarkdown {
  readonly next: number;
  readonly closed: boolean;
  readonly closingStart?: number;
}

const escapable = new Set(["\\", "*", "_", "[", "]", "(", ")"]);

export function compileMarkdown(source: string): CompiledMarkdown {
  const result = parseRange(source, 0, source.length);
  const seen = new Set<string>();
  const facets = result.facets.filter((facet) => {
    const key = `${facet.$type}:${facet.start}:${facet.end}:${"uri" in facet ? facet.uri : ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return {
    text: result.text,
    facets,
    tokens: [...result.tokens].sort((a, b) => a.start - b.start || a.end - b.end),
    sources: result.sources,
  };
}

function parseRange(source: string, start: number, end: number, stop?: string): ParseResult {
  let position = start;
  let text = "";
  const facets: TextFacet[] = [];
  const tokens: MarkdownToken[] = [];
  const sources: number[] = [];

  while (position < end) {
    if (stop && matchesStop(source, position, stop)) {
      return {
        text,
        facets,
        tokens,
        sources,
        next: position + stop.length,
        closed: true,
        closingStart: position,
      };
    }

    if (
      source[position] === "\\" && position + 1 < end &&
      escapable.has(source[position + 1]!)
    ) {
      text += source[position + 1];
      sources.push(position + 1);
      tokens.push({ start: position, end: position + 1, kind: "escape" });
      position += 2;
      continue;
    }

    if (source[position] === "[") {
      const link = parseLink(source, position, end);
      if (link) {
        append(link.result, text.length, facets, tokens, sources);
        text += link.result.text;
        position = link.next;
        continue;
      }
    }

    let matched = false;
    for (
      const [delimiter, styles] of [
        ["***", ["bold", "italic"]],
        ["**", ["bold"]],
        ["*", ["italic"]],
        ["_", ["italic"]],
      ] as const
    ) {
      if (!source.startsWith(delimiter, position) || !canOpen(source, position, delimiter)) {
        continue;
      }
      const inner = parseRange(source, position + delimiter.length, end, delimiter);
      if (!inner.closed || inner.text.length === 0) continue;

      const offset = text.length;
      append(inner, offset, facets, tokens, sources);
      text += inner.text;
      for (const style of styles) {
        facets.push({
          $type: `blue.cerulea.app.facet#${style}`,
          start: offset,
          end: text.length,
        });
      }
      tokens.push(
        { start: position, end: position + delimiter.length, kind: styles[0] },
        { start: inner.closingStart!, end: inner.next, kind: styles[0] },
      );
      matched = true;
      position = inner.next;
      break;
    }
    if (matched) continue;

    text += source[position];
    sources.push(position);
    position++;
  }

  return { text, facets, tokens, sources, next: end, closed: false };
}

function parseLink(
  source: string,
  start: number,
  end: number,
): { readonly result: CompiledMarkdown; readonly next: number } | undefined {
  let labelEnd = -1;
  let depth = 0;
  for (let position = start + 1; position < end; position++) {
    if (source[position] === "\\" && position + 1 < end) {
      position++;
      continue;
    }
    if (source[position] === "[") depth++;
    else if (source[position] === "]") {
      if (depth > 0) depth--;
      else if (source[position + 1] === "(") {
        labelEnd = position;
        break;
      }
    }
  }
  if (labelEnd < 0) return undefined;

  let destinationEnd = -1;
  depth = 0;
  for (let position = labelEnd + 2; position < end; position++) {
    if (source[position] === "\\" && position + 1 < end) {
      position++;
      continue;
    }
    if (source[position] === "(") depth++;
    else if (source[position] === ")") {
      if (depth > 0) depth--;
      else {
        destinationEnd = position;
        break;
      }
    }
  }
  if (destinationEnd < 0) return undefined;

  const rawDestination = source.slice(labelEnd + 2, destinationEnd);
  const destination = rawDestination.replace(/\\(.)/g, "$1");
  const uri = safeLink(destination);
  if (!uri) return undefined;

  const label = parseRange(source, start + 1, labelEnd);
  if (label.text.length === 0) return undefined;
  const facets = [
    ...label.facets,
    {
      $type: "blue.cerulea.app.facet#link",
      start: 0,
      end: label.text.length,
      uri,
    } satisfies TextFacet,
  ];
  const tokens = [
    ...label.tokens,
    { start, end: start + 1, kind: "link-marker" } satisfies MarkdownToken,
    { start: labelEnd, end: labelEnd + 2, kind: "link-marker" } satisfies MarkdownToken,
    {
      start: labelEnd + 2,
      end: destinationEnd,
      kind: "link-destination",
    } satisfies MarkdownToken,
    { start: destinationEnd, end: destinationEnd + 1, kind: "link-marker" } satisfies MarkdownToken,
  ];
  return {
    result: { text: label.text, facets, tokens, sources: label.sources },
    next: destinationEnd + 1,
  };
}

function append(
  child: CompiledMarkdown,
  offset: number,
  facets: TextFacet[],
  tokens: MarkdownToken[],
  sources: number[],
): void {
  for (const facet of child.facets) {
    facets.push({ ...facet, start: facet.start + offset, end: facet.end + offset });
  }
  tokens.push(...child.tokens);
  // A loop, not a spread: long posts exceed the engine's argument limit.
  for (const index of child.sources) sources.push(index);
}

function matchesStop(source: string, position: number, stop: string): boolean {
  if (!source.startsWith(stop, position) || !canClose(source, position, stop)) return false;
  if (stop === "*") {
    let length = 0;
    while (source[position + length] === "*") length++;
    return length % 2 === 1;
  }
  if (stop === "_") return source[position + 1] !== "_";
  return true;
}

function canOpen(source: string, position: number, delimiter: string): boolean {
  const before = source[position - 1];
  const after = source[position + delimiter.length];
  return !!after && !/\s/.test(after) &&
    !(delimiter === "_" && isWord(before) && isWord(after));
}

function canClose(source: string, position: number, delimiter: string): boolean {
  const before = source[position - 1];
  const after = source[position + delimiter.length];
  return !!before && !/\s/.test(before) &&
    !(delimiter === "_" && isWord(before) && isWord(after));
}

function isWord(value: string | undefined): boolean {
  return value !== undefined && /[\p{Letter}\p{Number}]/u.test(value);
}

function safeLink(value: string): string | undefined {
  if (!value || /\s/.test(value)) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Rebuild composer source from stored text and facets, for editing. Only emphasis and links are
 * expressible; mention and tag facets from other clients are dropped along with any facets that
 * overlap without nesting.
 */
export function markdownSource(text: string, facets: readonly TextFacet[]): string {
  const markers = facets.flatMap((facet): [TextFacet, string, string][] => {
    switch (facet.$type) {
      case "blue.cerulea.app.facet#bold":
        return [[facet, "**", "**"]];
      case "blue.cerulea.app.facet#italic":
        return [[facet, "*", "*"]];
      case "blue.cerulea.app.facet#link":
        return [[facet, "[", `](${facet.uri.replace(/[\\)]/g, "\\$&")})`]];
      default:
        return [];
    }
  }).filter(([facet]) => facet.start < facet.end && facet.end <= text.length)
    .sort(([a], [b]) => a.start - b.start || b.end - a.end);
  const open: typeof markers = [];
  let source = "";
  let position = 0;
  const emit = (end: number) => {
    source += text.slice(position, end).replace(/[\\*_[\]]/g, "\\$&");
    position = end;
  };
  for (const marker of markers) {
    while (open.length && open.at(-1)![0].end <= marker[0].start) {
      const closing = open.pop()!;
      emit(closing[0].end);
      source += closing[2];
    }
    if (open.length && open.at(-1)![0].end < marker[0].end) continue;
    emit(marker[0].start);
    source += marker[1];
    open.push(marker);
  }
  while (open.length) {
    const closing = open.pop()!;
    emit(closing[0].end);
    source += closing[2];
  }
  emit(text.length);
  return source;
}
